import { lockScroll, unlockScroll } from '@theme/dialog';

/* V2 search overlay: full-screen dialog (non-modal + aria-modal, see showDialog), predictive results via the Section Rendering API,
   keyboard navigation through results, editor-safe lifecycle. */
const SECTION = 'xz-v2-predictive-search';
const EASE = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
const EASE_IN = 'cubic-bezier(0.55, 0.085, 0.68, 0.53)';
const TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// While a nav overlay is open, hide Google's merchant badge (fixed, z-index 2^31-1, above any overlay).
// Shared with the header (mega panels) and the mobile menu through window.XZV2Overlays.
function setOverlay(key, on) {
  const keys = window.XZV2Overlays || (window.XZV2Overlays = new Set());
  if (on) keys.add(key); else keys.delete(key);
  document.documentElement?.classList?.toggle('xz-v2-overlay-open', keys.size > 0);
}

class XinzuoV2Search extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    const { signal } = this.events;
    this.dialog = this.querySelector('[data-v2-search-dialog]');
    this.input = this.querySelector('[data-v2-search-input]');
    this.clear = this.querySelector('[data-v2-search-clear]');
    this.live = this.querySelector('[data-v2-search-live]');
    this.idle = this.querySelector('[data-v2-search-idle]');
    this.results = this.querySelector('[data-v2-search-results]');
    this.scroller = this.querySelector('[data-v2-search-scroll]');
    this.status = this.querySelector('[data-v2-search-status]');
    this.errorTemplate = this.querySelector('template[data-v2-search-error]');
    this.cache = new Map();

    // Small phones get a placeholder that fits the input (the long one is the no-JS default).
    const small = window.matchMedia('(max-width: 479px)');
    if (!this.input.dataset.v2PlaceholderLong) this.input.dataset.v2PlaceholderLong = this.input.placeholder;
    const syncPlaceholder = () => {
      this.input.placeholder = small.matches ? this.input.dataset.v2PlaceholderShort || this.input.dataset.v2PlaceholderLong : this.input.dataset.v2PlaceholderLong;
    };
    syncPlaceholder();
    if (small.addEventListener) small.addEventListener('change', syncPlaceholder, { signal });
    else window.addEventListener('resize', syncPlaceholder, { passive: true, signal });

    this.querySelector('[data-v2-search-close]').addEventListener('click', () => this.closeDialog(), { signal });
    this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.closeDialog(); }, { signal });
    // Opened non-modally (see showDialog): the overlay owns Escape and keeps Tab inside itself.
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !event.defaultPrevented) { event.preventDefault(); this.closeDialog(); return; }
      if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
      const items = [...this.dialog.querySelectorAll(TABBABLE)].filter((el) => el.getClientRects().length > 0);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }, { signal });
    document.addEventListener('focusin', (event) => {
      // The Klaviyo signup popup sits above this overlay (xz-v2-home-body.css); let focus stay in it.
      if (event.target.closest?.('[data-kl-scroll-locking-modal]')) return;
      if (this.dialog.open && !this.closing && !this.dialog.contains(event.target)) this.input.focus({ preventScroll: true });
    }, { signal });
    this.dialog.addEventListener('close', () => this.restore(), { signal });
    this.dialog.addEventListener('click', (event) => {
      // Clicks on the overlay backdrop area outside the frame close it.
      if (event.target === this.dialog || event.target === this.scroller) this.closeDialog();
    }, { signal });
    this.input.addEventListener('input', () => this.queue(), { signal });
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') { event.preventDefault(); this.move(0); }
      // A native search field swallows the first Escape to clear itself; close in one press instead.
      else if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.closeDialog(); }
    }, { signal });
    this.results.addEventListener('keydown', (event) => {
      const items = this.items();
      const index = items.indexOf(document.activeElement);
      if (index < 0) return;
      if (event.key === 'ArrowDown') { event.preventDefault(); this.move(index + 1); }
      else if (event.key === 'ArrowUp') { event.preventDefault(); index === 0 ? this.input.focus() : this.move(index - 1); }
    }, { signal });
    this.clear.addEventListener('click', () => { this.input.value = ''; this.render(''); this.input.focus(); }, { signal });
    this.querySelector('form').addEventListener('submit', (event) => {
      if (!this.input.value.trim()) { event.preventDefault(); this.input.focus(); }
    }, { signal });
    document.addEventListener('keydown', (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key?.toLowerCase() === 'k' && !event.altKey) {
        // Leave other fields' own shortcuts alone (only our own input toggles the overlay).
        const target = event.target;
        const editable = target instanceof HTMLElement && target !== this.input && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));
        if (editable) return;
        event.preventDefault();
        this.dialog.open ? this.closeDialog() : this.showDialog(document.activeElement);
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.events?.abort();
    this.fetching?.abort();
    clearTimeout(this.timer);
    if (this.dialog?.open) this.dialog.close();
    if (this.dialog) unlockScroll(this.dialog);
    setOverlay('search', false);
  }

  get reduced() { return matchMedia('(prefers-reduced-motion: reduce)').matches; }

  showDialog(opener) {
    if (this.dialog.open) { this.input.focus(); return; }
    const active = document.activeElement;
    this.opener = opener instanceof HTMLElement ? opener : (active instanceof HTMLElement && active !== document.body ? active : null);
    // The black overlay shows at once and its content fades/slides in, as with the modal's black ::backdrop
    // before. Non-modal on purpose: showModal() makes the page inert, which Chrome applies by restyling every
    // element in the click frame (about 20ms at 390, 80ms+ on a 4x-slower phone) and delayed the fade start.
    // The dialog is fixed, full-viewport and above the page (z-index), carries aria-modal="true", traps Tab
    // and closes on Escape (listeners above); lockScroll keeps the page still behind it.
    this.scroller.style.transition = 'none';
    this.scroller.style.opacity = '0';
    this.scroller.style.transform = 'translateY(-24px)';
    this.dialog.show();
    lockScroll(this.dialog);
    setOverlay('search', true);
    this.setAttribute('open', '');
    if (this.opener?.matches('[data-v2-search]')) this.opener.setAttribute('aria-expanded', 'true');
    this.input.focus({ preventScroll: true });
    requestAnimationFrame(() => {
      this.scroller.style.transition = this.reduced ? 'none' : `opacity .2s ${EASE}, transform .5s ${EASE}`;
      this.scroller.style.opacity = '1';
      this.scroller.style.transform = 'translateY(0)';
    });
  }

  closeDialog() {
    if (!this.dialog.open || this.closing) return;
    this.closing = true;
    let fallback = 0;
    const finish = (event) => {
      if (event && (event.target !== this.scroller || event.propertyName !== 'opacity')) return;
      this.scroller.removeEventListener('transitionend', finish);
      clearTimeout(fallback);
      if (!this.closing) return;
      this.closing = false;
      if (this.dialog.open) this.dialog.close();
    };
    if (this.reduced) return finish();
    // Milen's menu close: 100ms inQuad on the content (the black overlay stays until it closes, as the
    // modal's ::backdrop did). Close on the fade's own end; the timer only covers a transition that never
    // fires (hidden tab).
    this.scroller.addEventListener('transitionend', finish);
    this.scroller.style.transition = `opacity .1s ${EASE_IN}`;
    this.scroller.style.opacity = '0';
    fallback = setTimeout(finish, 250);
  }

  restore() {
    unlockScroll(this.dialog);
    setOverlay('search', false);
    this.removeAttribute('open');
    clearTimeout(this.timer);
    this.fetching?.abort();
    this.input.value = '';
    this.clear.hidden = true;
    this.render('');
    this.closing = false;
    if (this.opener?.matches('[data-v2-search]')) this.opener.setAttribute('aria-expanded', 'false');
    if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    this.opener = null;
  }

  items() { return [...this.results.querySelectorAll('[data-v2-search-item]')].filter((el) => el.offsetParent !== null); }
  move(index) { const items = this.items(); if (items.length) items[Math.min(index, items.length - 1)].focus(); }

  queue() {
    clearTimeout(this.timer);
    const term = this.input.value.trim();
    this.clear.hidden = !term;
    if (!term) { this.render(''); return; }
    this.timer = setTimeout(() => this.fetchResults(term), 200);
  }

  async fetchResults(term, attempt = 0) {
    if (this.cache.has(term)) { this.render(term, this.cache.get(term)); return; }
    this.fetching?.abort();
    const controller = new AbortController();
    this.fetching = controller;
    this.results.setAttribute('aria-busy', 'true');
    const url = new URL((window.Shopify?.routes?.root || '/') + 'search/suggest', location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', SECTION);
    url.searchParams.set('resources[type]', 'product,collection,article,page,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][prefix]', 'last');
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.querySelector('.xz-v2-search-live');
      if (!node) throw new Error('no results markup');
      this.cache.set(term, node);
      if (this.input.value.trim() === term) this.render(term, node);
    } catch (error) {
      if (error.name === 'AbortError' || this.input.value.trim() !== term) return;
      // One quiet retry (a 429 under load usually clears); typing again or closing cancels it via this.timer.
      if (attempt === 0) this.timer = setTimeout(() => { if (this.input.value.trim() === term) this.fetchResults(term, 1); }, 1000);
      else this.render(term, null);
    } finally {
      if (this.fetching === controller) { this.fetching = null; this.results.setAttribute('aria-busy', 'false'); }
    }
  }

  // Visible failure state: a message plus VIEW ALL for the typed term, so a failed request never looks
  // like an empty search (the idle popular tags still show underneath).
  errorNode(term) {
    const node = this.errorTemplate?.content.firstElementChild?.cloneNode(true);
    const cta = node?.querySelector('[data-v2-search-base]');
    if (cta) {
      const params = new URLSearchParams({ q: term, type: 'product', 'options[prefix]': 'last' });
      cta.href = `${cta.dataset.v2SearchBase}?${params}`;
    }
    return node || null;
  }

  render(term, node) {
    if (!node && term) node = this.errorNode(term);
    this.live.replaceChildren(...(node ? [node.cloneNode(true)] : []));
    const count = node ? Number(node.dataset.v2SearchCount || 0) : 0;
    // Idle content (popular tags, best sellers) shows before typing and under "no results".
    this.idle.hidden = !!term && count > 0;
    const best = this.idle.querySelector('[data-v2-search-best]');
    if (best) best.hidden = !!term;
    // One persistent live region; only its text changes, so screen readers announce each result count.
    if (this.status) this.status.textContent = term ? (node?.dataset.v2SearchAnnounce || (node ? '' : 'Suggestions are unavailable right now. Press Enter to search all products.')) : '';
    this.scroller.scrollTop = 0;
  }
}

if (!customElements.get('xz-v2-search')) customElements.define('xz-v2-search', XinzuoV2Search);
